/**
 * `<page-generations>` — the publish history, routed at
 * `/instances/:id/generations`.
 *
 * Implements `SC-F600` (immutable, monotonically numbered generations, with the
 * draft distinguished from the live one everywhere), `SC-F608` (both digests, and
 * the fact that their equality — not the fact that we sent something — is what
 * makes a publish successful), `SC-F610` (rollback rolls **forward** to an older
 * desired state: a new generation whose content equals the selected one, because
 * "back two versions" is not a primitive anywhere in the stack), `SC-F611` (who
 * changed what, when, why and whether it landed, including the attempts that did
 * not) and `SC-F619` (the bundle download, per generation).
 *
 * Stories: `SC-S600`, `SC-S608`, `SC-S609`, `SC-S610`, `SC-S614`.
 *
 * **The history is not filtered to successes.** A `publish_failed` row stays in
 * place with its failure reason and with the previous generation still marked
 * live, because a history that hides failures cannot answer "did it land" — which
 * is the only question anybody opens this screen to ask.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  publishClassVariant,
  toast,
  toastPersistent,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
} from "../components/index.js";

import { ApiError, errorDetail, parseError } from "../api/errors.js";
import {
  downloadGenerationBundle,
  bundleFilename,
  listGenerations,
  restartClass,
  CHANGE_CLASS_LABELS,
  GENERATION_STATE_LABELS,
  type Generation,
} from "../api/generations.js";
import { getInstance, type Instance } from "../api/instances.js";
import { jobFailure, watchJob, type Job } from "../api/jobs.js";
import {
  getDrift,
  rollback,
  type DriftReport,
  type DriftState,
  type PublishResult,
} from "../api/publish.js";
import { hrefFor, navigate } from "../nav.js";

/** The six stored states, each with the tone it is allowed to carry.
 * `publish_failed` is an error and `superseded` is not: one of them means
 * something went wrong, the other means time passed. */
const STATE_TONE: Record<string, BnBadgeVariant> = {
  draft: "neutral",
  validated: "info",
  publishing: "info",
  active: "success",
  publish_failed: "error",
  superseded: "neutral",
};

/** The four drift verdicts, tone-mapped (`SC-F609`).
 *
 * `undetermined` is `neutral` and not `warning`, deliberately: "we could not
 * compare" is the state every instance sits in for the first seconds of every task
 * start and for its whole life before the first publish, and colouring that amber
 * would train a reader to ignore the one tone that matters. */
const DRIFT_TONE: Record<DriftState, BnBadgeVariant> = {
  in_sync: "success",
  drifted: "error",
  undetermined: "neutral",
  unavailable: "warning",
};

const DRIFT_LABEL: Record<DriftState, string> = {
  in_sync: "No drift detected",
  drifted: "Drift detected",
  undetermined: "Nothing to compare",
  unavailable: "Instance unreachable",
};

const COLUMNS: BnColumn[] = [
  { key: "gen", label: "Generation", width: "1px", align: "right" },
  { key: "state", label: "State", width: "1px" },
  { key: "class", label: "Change", width: "1px" },
  { key: "who", label: "Published by" },
  { key: "digest", label: "Digest", mono: true },
  { key: "actions", label: "", width: "1px", align: "right" },
];

@customElement("page-generations")
export class PageGenerations extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private rows: Generation[] = [];
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  @state() private instance: Instance | null = null;

  /** The rollback ceremony. */
  @state() private target: Generation | null = null;
  @state() private message = "";
  @state() private rollingBack = false;
  @state() private rollbackFailure: ApiError | null = null;
  /** The classifier's own blast-radius text, as it arrived on a
   * `409 restart_required`. It is rendered VERBATIM: it is the sentence the API
   * refused us with, and paraphrasing it into reassurance is the failure `02` §5.3
   * says trains users to ignore the warning. */
  @state() private blastRadius = "";
  @state() private queryLossWarning = "";
  @state() private acceptRestart = false;
  /** The lockout confirmation of `03` §7.5: the operator types the instance name. */
  @state() private confirmName = "";
  @state() private lockoutBlocked = false;
  @state() private overrideReason = "";

  @state() private job: Job<PublishResult> | null = null;

  /** `SC-F609`'s standing verdict. Null while it is loading or after a failure —
   * the failure is its own state below rather than a fabricated "no drift". */
  @state() private drift: DriftReport | null = null;
  @state() private driftLoading = true;
  @state() private driftFailure: ApiError | null = null;

  private watchAbort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 72ch;
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    /* The drift panel's inline groups. It is a flex row rather than a grid so a
       badge set wraps at a narrow width instead of overflowing its card. */
    .row {
      display: flex;
      gap: var(--bino-space-xs);
      flex-wrap: wrap;
    }
    .num {
      font-family: var(--bino-font-mono);
      font-weight: 600;
      color: var(--bino-primary);
      text-decoration: none;
    }
    .num:hover {
      text-decoration: underline;
    }
    .num:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .cell-actions {
      display: flex;
      gap: var(--bino-space-xs);
      justify-content: flex-end;
      flex-wrap: wrap;
    }
    .failed {
      margin: var(--bino-space-xs) 0 0;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      max-width: 56ch;
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-size: var(--bino-font-size-sm);
      white-space: pre-wrap;
      overflow-x: auto;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.watchAbort?.abort();
  }

  /** Driven from `willUpdate` alone: the router sets `instanceId` before the first
   * update, so loading from `connectedCallback` too would fetch twice. */
  override willUpdate(changed: PropertyValues<this>) {
    if (changed.has("instanceId") && this.instanceId) {
      void this.load();
      void this.loadDrift();
    }
  }

  /** Loads the drift verdict independently of the history.
   *
   * Separate on purpose: the drift read reaches the instance's admin plane and the
   * history does not, so folding them into one `Promise.all` would make an
   * unreachable instance blank a page whose whole content is in our own database. */
  private async loadDrift() {
    this.driftLoading = true;
    try {
      this.drift = await getDrift(this.instanceId);
      this.driftFailure = null;
    } catch (err) {
      this.driftFailure = parseError(err);
      this.drift = null;
    } finally {
      this.driftLoading = false;
    }
  }

  private async load() {
    this.loading = true;
    try {
      const [page, instance] = await Promise.all([
        listGenerations(this.instanceId),
        getInstance(this.instanceId).catch(() => null),
      ]);
      this.rows = page.items;
      this.instance = instance;
      this.failure = null;
    } catch (err) {
      this.failure = parseError(err);
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  /* ── the bundle download (`SC-F619`) ─────────────────────────────── */

  private async download(gen: Generation) {
    try {
      const blob = await downloadGenerationBundle(this.instanceId, gen.generation);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bundleFilename(this.instance?.name ?? "instance", gen.generation);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast(
        `Generation ${gen.generation} downloaded. server.yaml's admin token is a placeholder; every other value is what the container sees.`,
        "success",
        10000,
      );
    } catch (err) {
      toast(parseError(err).message, "error");
    }
  }

  /* ── rollback (`SC-F610`) ────────────────────────────────────────── */

  private openRollback(gen: Generation) {
    this.target = gen;
    this.message = `Roll forward to the content of generation ${gen.generation}`;
    this.rollbackFailure = null;
    this.blastRadius = "";
    this.queryLossWarning = "";
    this.acceptRestart = false;
    this.lockoutBlocked = false;
    this.confirmName = "";
    this.overrideReason = "";
  }

  private closeRollback() {
    this.target = null;
    this.rollingBack = false;
  }

  private async confirmRollback() {
    const target = this.target;
    if (!target) return;
    this.rollingBack = true;
    this.rollbackFailure = null;
    try {
      const started = await rollback(this.instanceId, target.generation, {
        message: this.message.trim() || undefined,
        allow_restart: this.acceptRestart || undefined,
        override_reason: this.overrideReason.trim() || undefined,
        confirm_instance_name: this.confirmName.trim() || undefined,
      });
      this.closeRollback();
      this.job = started.job;
      await this.watch(started.job.id);
    } catch (err) {
      const failure = parseError(err);
      this.rollbackFailure = failure;
      this.rollingBack = false;
      // The refusal carries the classifier's own prose. `restart_required` means
      // this rollback crosses a restart boundary relative to what is live — which
      // is a different question from what the target generation's own stored class
      // was, and the reason this dialog does not guess it up front.
      if (failure.code === "restart_required") {
        this.blastRadius = errorDetail<string>(failure, "blast_radius") ?? "";
        this.queryLossWarning = errorDetail<string>(failure, "query_loss_warning") ?? "";
      }
      if (errorDetail<string>(failure, "reason") === "lockout_confirmation_required") {
        this.lockoutBlocked = true;
      }
    }
  }

  private async watch(jobId: string) {
    this.watchAbort?.abort();
    this.watchAbort = new AbortController();
    try {
      const done = await watchJob<PublishResult>(jobId, {
        onUpdate: (job) => (this.job = job),
        signal: this.watchAbort.signal,
      });
      const failed = jobFailure(done);
      if (failed) {
        toastPersistent(failed.message, "error", "Rollback failed");
      } else if (done.result) {
        const result = done.result;
        toastPersistent(
          `Generation ${result.generation} is live. The instance read back the digest we rendered (${short(result.observed_digest)}), which is what makes this true.`,
          "success",
          "Rollback confirmed",
        );
      }
      await this.load();
    } catch {
      // The watch broke, not the job. It is still running server-side, and the
      // history below is the authority — so the page reloads rather than claiming
      // an outcome it did not observe.
      await this.load();
    }
  }

  /* ── rendering ───────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /**
   * The drift panel (`SC-F609`).
   *
   * **The granularity note is rendered on every verdict, not only on a finding.**
   * "Something in your configuration is not what we published" and "your
   * tenant-isolation row filter is not what we published" are different claims, and
   * only the first is supportable at the pinned Sluice build — `GET /admin/policies`
   * returns a name, a priority and an enforcement mode per kind plus one directory
   * digest, and nothing finer. A limitation that appeared only alongside a finding
   * would be a limitation nobody reads until the moment they are least able to
   * evaluate it.
   */
  private renderDrift() {
    if (this.driftLoading) {
      return html`<bn-card heading="Configuration drift">
        <div class="row" style="align-items:center;gap:var(--bino-space-sm)">
          <bn-spinner size="sm"></bn-spinner>
          <span class="meta">Comparing the live digest with the confirmed generation…</span>
        </div>
      </bn-card>`;
    }
    if (this.driftFailure) {
      return html`<bn-card heading="Configuration drift">
        <div class="failure" role="alert">
          <p>
            The drift check could not be read. The publish history below is unaffected — it comes
            from the control plane's own records.
          </p>
          <p>${this.driftFailure.message}</p>
          <bn-kv .rows=${this.supportRows(this.driftFailure)}></bn-kv>
          <div>
            <bn-button @click=${() => void this.loadDrift()}>Try again</bn-button>
          </div>
        </div>
      </bn-card>`;
    }
    const d = this.drift;
    if (!d) return nothing;

    const rows: BnKvRow[] = [
      {
        label: "Compared against",
        value: d.generation
          ? `generation ${d.generation.number} (${d.generation.state})`
          : undefined,
      },
      { label: "Digest we published", value: d.generation?.rendered_digest },
      {
        label: "Digest the instance confirmed",
        value: d.generation?.confirmed_digest || undefined,
      },
      {
        label: "Confirmed at",
        value: d.generation?.confirmed_at ? when(d.generation.confirmed_at) : undefined,
      },
      { label: "Digest the instance reports now", value: d.observed?.config_digest },
      {
        label: "Snapshot version",
        value: d.observed ? String(d.observed.config_version) : undefined,
      },
      { label: "Checked", value: when(d.observed_at) },
    ];

    return html`<bn-card heading="Configuration drift">
      <div slot="actions" class="row">
        <bn-button size="sm" @click=${() => void this.loadDrift()}>Re-check</bn-button>
      </div>
      <div class="stack">
        <div class="row" style="align-items:center;gap:var(--bino-space-sm);flex-wrap:wrap">
          <bn-badge .variant=${DRIFT_TONE[d.state]} ?dot=${d.state === "drifted"}>
            ${DRIFT_LABEL[d.state]}
          </bn-badge>
          <bn-badge variant="neutral">${d.granularity === "digest_only" ? "digest only" : d.granularity}</bn-badge>
          ${d.observed?.degraded
            ? html`<bn-badge variant="warning">instance degraded</bn-badge>`
            : nothing}
        </div>
        <p class="copy">${d.detail}</p>
        <bn-kv .rows=${rows} unsetText="not available"></bn-kv>
        ${d.remedy ? html`<p class="copy"><strong>What to do.</strong> ${d.remedy}</p>` : nothing}
        ${d.repair_guard ? html`<p class="copy">${d.repair_guard}</p>` : nothing}
        ${d.draft
          ? html`<bn-panel eyebrow=${`Draft ${d.draft.number} is open`}>
              <p class="copy">${d.draft.note}</p>
            </bn-panel>`
          : nothing}
        <bn-panel eyebrow="How precise this answer is">
          <p class="copy">${d.granularity_note}</p>
          <p class="copy">${d.upstream_note}</p>
        </bn-panel>
        ${d.classes.length
          ? html`<div>
              <h2>What this check does and does not cover</h2>
              ${d.classes.map(
                (c) => html`<p class="copy">
                  <strong>${c.code}</strong>
                  <bn-badge .variant=${c.observable ? "info" : "neutral"}>
                    ${c.observable ? "checked here" : "not checked here"}
                  </bn-badge>
                  ${c.note}
                </p>`,
              )}
            </div>`
          : nothing}
      </div>
    </bn-card>`;
  }

  private toRows(): BnRow[] {
    const live = this.rows.find((g) => g.state === "active");
    return this.rows.map((g) => ({
      gen: html`<a class="num" href=${hrefFor("instances", this.instanceId, "generations", g.generation)}>
        #${g.generation}
      </a>`,
      state: html`<bn-badge .variant=${STATE_TONE[g.state] ?? "neutral"}>
          ${GENERATION_STATE_LABELS[g.state] ?? g.state}
        </bn-badge>
        ${g.state === "publish_failed"
          ? html`<span class="sub">
              ${live ? `generation ${live.generation} is still live` : "nothing became live"}
            </span>`
          : nothing}`,
      class: g.change_class
        ? html`<bn-badge .variant=${publishClassVariant(g.publish_path ?? g.change_class)}>
              ${restartClass(g.change_class) ? "restart" : "reload"}
            </bn-badge>
            <span class="sub">${CHANGE_CLASS_LABELS[g.change_class] ?? g.change_class}</span>`
        : html`<span class="sub">—</span>`,
      who: html`<span>${g.created_by_label || g.created_by || "system"}</span>
        <span class="sub">${when(g.published_at || g.created_at)}</span>
        ${g.message ? html`<span class="sub">“${g.message}”</span>` : nothing}
        ${g.override_reason
          ? html`<span class="sub">override: “${g.override_reason}”</span>`
          : nothing}
        ${g.failure_reason ? html`<p class="failed">${g.failure_reason}</p>` : nothing}`,
      digest: html`<span>${short(g.rendered_digest ?? "")}</span>
        ${g.confirmed_digest && g.confirmed_digest !== g.rendered_digest
          ? html`<span class="sub">instance reported ${short(g.confirmed_digest)}</span>`
          : nothing}
        ${g.confirmed_digest && g.confirmed_digest === g.rendered_digest
          ? html`<span class="sub">confirmed by the instance</span>`
          : nothing}`,
      actions: html`<span class="cell-actions">
        <bn-button
          size="sm"
          @click=${() => navigate(hrefFor("instances", this.instanceId, "generations", g.generation, "yaml"))}
        >
          YAML
        </bn-button>
        <bn-button size="sm" @click=${() => void this.download(g)}>Bundle</bn-button>
        ${g.state === "active" || g.state === "draft" || g.state === "publishing"
          ? nothing
          : html`<bn-button
              size="sm"
              ?disabled=${!!g.rollback_blocked_reason}
              @click=${() => this.openRollback(g)}
            >
              Roll forward to this
            </bn-button>`}
      </span>
      ${g.rollback_blocked_reason
        ? html`<span class="sub">${g.rollback_blocked_reason}</span>`
        : nothing}`,
    }));
  }

  /**
   * The rollback dialog. The wording is the feature: a rollback publishes a NEW
   * generation whose content equals the selected one, so the history stays
   * append-only and the audit trail never shows time moving backwards.
   */
  private renderRollback() {
    const target = this.target;
    if (!target) return nothing;
    const needsRestart = this.blastRadius !== "";
    return html`<bn-modal
      open
      .width=${640}
      tone=${needsRestart ? "destructive" : "default"}
      heading=${`Roll forward to the content of generation ${target.generation}?`}
      @close=${() => this.closeRollback()}
    >
      <p class="copy">
        This does not rewind anything. It publishes a <strong>new</strong> generation whose content
        equals generation ${target.generation} — Kubernetes keeps no previous Pod spec, so "back
        two versions" is not a primitive anywhere in this stack, and rolling forward is what makes
        any target reachable.
      </p>
      <p class="copy">
        The change class is computed against what is <strong>live now</strong>, not against what was
        live when generation ${target.generation} was published. A rollback that only moves policies
        takes the reload path with no restart; one that crosses a datasource, secret, server-setting,
        size or image boundary replaces the task.
      </p>
      <bn-field
        label="Message"
        hint="The only human record of why this generation exists. It appears in the history next to your name."
      >
        <bn-input
          .value=${this.message}
          @input=${(e: CustomEvent<{ value: string }>) => (this.message = e.detail.value)}
        ></bn-input>
      </bn-field>
      ${this.lockoutBlocked
        ? html`<div style="margin-top: var(--bino-space-md)">
            <bn-field
              label="Type the instance name to confirm"
              hint="This generation leaves the instance with no allow rule: every query is denied. Typing the name is how that becomes a decision rather than a surprise."
            >
              <bn-input
                mono
                .value=${this.confirmName}
                placeholder=${this.instance?.name ?? ""}
                @input=${(e: CustomEvent<{ value: string }>) => (this.confirmName = e.detail.value)}
              ></bn-input>
            </bn-field>
            <bn-field label="Why are you publishing it anyway?" required>
              <bn-input
                .value=${this.overrideReason}
                @input=${(e: CustomEvent<{ value: string }>) =>
                  (this.overrideReason = e.detail.value)}
              ></bn-input>
            </bn-field>
          </div>`
        : nothing}
      ${this.rollbackFailure && !needsRestart
        ? html`<div role="alert" style="margin-top: var(--bino-space-md)">
            <p class="copy">${this.rollbackFailure.message}</p>
            <bn-kv .rows=${this.supportRows(this.rollbackFailure)}></bn-kv>
          </div>`
        : nothing}
      ${needsRestart
        ? html`<div slot="consequence">
            <p style="margin: 0 0 var(--bino-space-sm)">${this.blastRadius}</p>
            ${this.queryLossWarning
              ? html`<p style="margin: 0 0 var(--bino-space-sm)">${this.queryLossWarning}</p>`
              : nothing}
            <bn-checkbox
              label="I accept the interruption this rollback causes"
              .checked=${this.acceptRestart}
              @change=${(e: CustomEvent<{ checked: boolean }>) =>
                (this.acceptRestart = e.detail.checked)}
            ></bn-checkbox>
          </div>`
        : nothing}
      <bn-button slot="footer" @click=${() => this.closeRollback()}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant=${needsRestart ? "danger" : "primary"}
        ?disabled=${needsRestart && !this.acceptRestart}
        ?loading=${this.rollingBack}
        @click=${() => void this.confirmRollback()}
      >
        ${needsRestart ? "Publish and restart" : "Roll forward"}
      </bn-button>
    </bn-modal>`;
  }

  /** The in-flight job, shown as its own step because `SC-F608` makes the digest
   * read-back the success condition rather than a formality. */
  private renderJob() {
    const job = this.job;
    if (!job || job.status === "succeeded") return nothing;
    return html`<bn-panel eyebrow="Rollback in progress">
      <p class="copy">
        ${job.progress
          ? `${job.progress.step} — step ${job.progress.index} of ${job.progress.total}`
          : `Job ${job.status}`}
      </p>
      <p class="copy">
        It is not recorded as published until the instance reports back the same digest the control
        plane rendered.
      </p>
    </bn-panel>`;
  }

  override render() {
    if (this.loading && this.rows.length === 0) {
      return html`<bn-spinner block size="28" label="Loading the publish history"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="Could not load the publish history">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>`;
    }
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Generations</h1>
          <p class="lede">
            Every publish creates one immutable, numbered snapshot: all eleven manifest kinds, the
            rendered server configuration, the secret inventory and the pinned image. Failed attempts
            stay in this list with their reason — a history filtered to successes cannot answer
            whether a change landed.
          </p>
        </div>
        <div>
          <bn-button
            variant="primary"
            @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}
          >
            Review the draft
          </bn-button>
        </div>
      </header>

      <div class="stack">
        ${this.renderJob()} ${this.renderDrift()}
        <bn-card flush>
          <bn-table
            caption="Publish history"
            .columns=${COLUMNS}
            .rows=${this.toRows()}
            ?loading=${this.loading}
          >
            <div slot="empty">
              <bn-empty-state
                icon="◷"
                heading="Nothing has been published yet"
                message="This instance is running an empty policy directory, which in Sluice is valid and means deny everything. Author a policy or connect a datasource, then publish: the first publish creates generation 1."
              >
                <bn-button
                  slot="actions"
                  variant="primary"
                  @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}
                >
                  Review the draft
                </bn-button>
              </bn-empty-state>
            </div>
          </bn-table>
        </bn-card>
        <bn-panel eyebrow="What a digest proves">
          <p class="copy">
            <code>Digest</code> is what the control plane computed when it rendered the bundle.
            <strong>Confirmed</strong> means the instance itself reported the same value back from
            <code>/admin/healthz</code> after the change was applied. That equality is the entire
            definition of a successful publish here: "we sent it" is not "it is running", and a
            mismatch or a timeout leaves the generation <code>publish_failed</code> with the previous
            one still live.
          </p>
        </bn-panel>
      </div>
      ${this.renderRollback()}
    `;
  }
}

/** short renders a digest the way a reader compares one — by eye, on the first and
 * last characters. */
function short(digest: string): string {
  if (!digest) return "—";
  const bare = digest.replace(/^sha256:/, "");
  return bare.length <= 20 ? bare : `${bare.slice(0, 10)}…${bare.slice(-6)}`;
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-generations": PageGenerations;
  }
}
